// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useDraggable } from '@dnd-kit/core';
import { Box, useTheme } from '@mui/system';
import {
  IconButton, Menu, MenuItem, Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppDispatch, useAppSelector } from 'Hooks';
import ProjectPreview from 'Assets/projectPreview.png';
import OverflowTooltipText from 'Components/OverflowTooltipText';
import ProjectFolderBadge from 'Components/ProjectFolderBadge';
import DeleteProjectDialog from 'Components/DeleteProjectDialog';
import MoveProjectToFolderDialog from 'Components/MoveProjectToFolderDialog';
import { resolveDeliverySchedule } from 'Utils/deliverySchedule';
import ProjectReferenceLine from 'Components/ProjectReferenceLine';
import { formatProjectDate, PROJECT_VALUE_UNSET } from 'Utils/projectDisplay';
import { isPrimaryPress } from 'Utils/pointerPress';
import { getStatusChipWithPhase, getStatusDotColor } from 'Features/map/EsriDashboardMap/ProjectOverviewCard/statusChip';
import stylesDef from './styles';
import { resolveProjectRoute } from '../resolveProjectRoute';
import { dataRequestBlocksDelete } from '../dataRequestBlocksDelete';
import { selectProjectInfo } from '../projectListSelector';
import { findFolderById, selectFolderList } from '../../folderList/folderListSelector';
import { ReadThumbnailThunk } from '../projectListThunk';
import { DRAG_ACTIVATION_DISTANCE } from '../dragSensors';

/* eslint-disable react/require-default-props */
type ProjectListRowProps = {
  // IProjectMapCoordinate rather than IProject, as the grid card takes: the dashboard renders
  // the complete set, and an IProject satisfies the same optional fields.
  project: IProjectMapCoordinate;
  handleMoveProjectToFolderSuccess: (project?: IProject, folderId?: string) => void;
  // Inside a folder page every row is in that folder, so naming it on each says nothing -
  // the same reason the grid card takes this.
  isInsideFolder: boolean;
  // No default: defaultProps is deprecated for function components.
  isSelected?: boolean;
};
/* eslint-enable react/require-default-props */

// AUT-6432: the dashboard's list view. Same project facts as the card, laid out as a row -
// thumbnail and name on the left, the reference and created date beneath, and the delivery,
// due and cost values as labelled columns on the right.
export const ProjectListRow = ({
  project, handleMoveProjectToFolderSuccess, isInsideFolder, isSelected,
}: ProjectListRowProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // One read of the project's derived state, feeding the destination, the status and dates,
  // and the permission rule the card applies: a project someone else created, one already
  // being processed, or one with imagery/raster work pending cannot be deleted from here.
  const projectInfo = useSelector((state) => selectProjectInfo(state, project));
  const {
    cannotDelete, orderFinalized, expressAIReady, orderEstimate, orderStatus, imageryRequest, rasterRequest,
    orderEstimatedDeliveryDate, deliveryDate, kmlOnlyHold,
  } = projectInfo;
  const { resources } = useAppSelector((state) => state.auth);
  const { esriViewer } = useFlags();
  // An ops trainer sees the training name in place of the customer's, as the card shows it.
  const opsTrainer = 'opsTrainer' in resources;
  const displayName = (opsTrainer ? project.training?.nameEditable : project.name) || 'Untitled project';
  // The same rule as the card, so the two presentations never send the user to different places.
  const projectUrl = resolveProjectRoute({
    projectId: project._id,
    orderFinalized: Boolean(orderFinalized),
    expressAIReady: Boolean(expressAIReady),
    esriViewer: Boolean(esriViewer),
    hasProjectDetailsTab: 'projectDetailsTab' in resources,
  });
  const canDelete = !cannotDelete && !dataRequestBlocksDelete(imageryRequest, rasterRequest);

  // Which folder the project is filed in, shown the same way the card shows it.
  const folderList = useAppSelector(selectFolderList);
  const projectFolder = useMemo(
    () => (isInsideFolder ? undefined : findFolderById(folderList, project.folderId)),
    [isInsideFolder, folderList, project.folderId],
  );

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);

  // The order behind the row's status and date is the one selectProjectInfo picked - the same
  // pick the card, the map rail and the pin card quote.
  // The row draws no pipeline of its own, so the chip names the stage: "In Progress - QC".
  const statusChip = getStatusChipWithPhase({ orderStatus, isEstimate: orderEstimate, ...kmlOnlyHold });
  const statusDotColor = getStatusDotColor(theme, statusChip.kind);
  const hasDeliverySchedule = resolveDeliverySchedule({
    orderStatus, deliveryDate: deliveryDate || undefined, deliveryEstimate: orderEstimatedDeliveryDate || undefined,
  }).state !== 'none';
  const dueDateText = hasDeliverySchedule ? formatProjectDate(deliveryDate) : PROJECT_VALUE_UNSET;

  // Raw S3 thumbnail URLs 403 against the private bucket, so the real image is resolved
  // through readThumbnail - lazily, once the row first scrolls into view.
  const hasThumbnail = Boolean(project.thumbnail && !project.thumbnail.includes('undefined'));
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true });
  useEffect(() => {
    // Not once per mount: the list is emptied and refilled on every sort and filter change, so
    // without the resolved check each of those re-fetches an image the browser already has.
    if (inView && hasThumbnail && !project.thumbnailResolved) dispatch(ReadThumbnailThunk(project._id));
  }, [inView, project.thumbnailResolved]);

  // Rows drag onto the folder cards above them, exactly as the grid's cards do - the list is
  // the dashboard's default view, so filing a project must not require switching to cards.
  const { listeners, setNodeRef, transform } = useDraggable({ id: project._id, data: { type: 'project' } });
  // One node, two refs: the drag source and the lazy-thumbnail observer.
  const setRowRef = useCallback((node: HTMLElement | null) => {
    setNodeRef(node);
    inViewRef(node);
  }, [setNodeRef, inViewRef]);

  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = (event: React.PointerEvent) => {
    if (!isPrimaryPress(event)) return;
    dragStart.current = { x: event.clientX, y: event.clientY };
    listeners?.onPointerDown?.(event);
  };

  // A release that barely moved is a click; anything further was a drag and must not navigate.
  // The same distance the drag sensor activates at, so a press is a click or a drag, never both.
  const handlePointerUp = (event: React.PointerEvent) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (!start || !isPrimaryPress(event)) return;
    const travelled = Math.abs(event.clientX - start.x) >= DRAG_ACTIVATION_DISTANCE
      || Math.abs(event.clientY - start.y) >= DRAG_ACTIVATION_DISTANCE;
    if (!travelled) navigate(projectUrl);
  };

  const valueColumn = (value: string, label: string, valueStyle?: object) => (
    <Box sx={styles.valueColumn}>
      <Typography sx={[styles.value, valueStyle]}>{value}</Typography>
      <Typography sx={styles.valueLabel}>{label}</Typography>
    </Box>
  );

  return (
    <>
      <Box
        ref={setRowRef}
        // How the list finds this project to scroll to it, the same way the map rail's cards
        // are found. On the row itself rather than a wrapper, so the row stays the flex item.
        data-project-id={project._id}
        role="button"
        tabIndex={0}
        sx={[styles.row, Boolean(transform) && styles.rowDragging(transform), isSelected && styles.rowSelected]}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) navigate(projectUrl);
        }}
      >
        <Box sx={styles.identityGroup}>
          <Box
            component="img"
            // The placeholder until the private-bucket URL is resolved, as the rail's cards do -
            // the raw URL is a guaranteed 403 per row.
            src={hasThumbnail && project.thumbnailResolved ? project.thumbnail : ProjectPreview}
            alt=""
            sx={styles.thumbnail}
            onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
              // An expired signed URL degrades to the placeholder, not a broken image.
              event.currentTarget.src = ProjectPreview;
            }}
          />
          <Box sx={styles.identity}>
            <Box sx={styles.nameRow}>
              <OverflowTooltipText text={displayName} sx={styles.name} />
            </Box>
            <Box sx={styles.metaRow}>
              <ProjectReferenceLine projectId={project._id} referenceId={project.referenceId} sx={styles.metaText} />
              {projectFolder && <ProjectFolderBadge folderName={projectFolder.name} variant="inline" />}
              <Box sx={styles.createdGroup}>
                <CalendarMonthIcon sx={styles.calendarIcon} fontSize="inherit" />
                <Typography sx={styles.metaText}>
                  <Box component="span" sx={styles.metaLabel}>Created on:</Box>
                  {` ${formatProjectDate(project.createdAt)}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={styles.values}>
          {valueColumn(dueDateText, 'Due Date')}
          {/* A reading like the dates rather than a chip beside the name, so all three line up
              down the list. Its own width because "Not submitted" and its dot outrun the
              column the dates sit in. */}
          <Box sx={[styles.valueColumn, styles.statusColumn]}>
            <Box sx={styles.statusRow}>
              <Box sx={styles.statusDot(statusDotColor)} />
              <Typography sx={styles.value}>{statusChip.label}</Typography>
            </Box>
            <Typography sx={styles.valueLabel}>Status</Typography>
          </Box>
          <IconButton
            aria-label="Project actions"
            sx={styles.menuButton}
            // The button sits inside the drag source, so its pointer events are stopped here:
            // otherwise pressing it would arm a drag, and releasing would open the project
            // (the row's click test runs on pointerup, before this button's click fires).
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {/* The menu and both dialogs are siblings of the row, not children: React events bubble
          through portals, so inside the row a press on a dialog button would arm a drag and its
          release would open the project. */}
      {/* Explicit anchoring: MUI's default overlays the menu on the button it came from, so
          it reads as appearing somewhere arbitrary. This drops it directly below the "...",
          aligned to the row's right edge. */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          sx={styles.menuItem}
          onClick={() => {
            window?.pendo?.track('Move To Folder');
            setMenuAnchor(null);
            setShowMoveDialog(true);
          }}
        >
          Move To...
        </MenuItem>
        {canDelete && (
          <MenuItem
            sx={styles.menuItem}
            onClick={() => {
              window?.pendo?.track('Delete Project');
              setMenuAnchor(null);
              setShowDeleteDialog(true);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      <DeleteProjectDialog
        showDialog={showDeleteDialog}
        setShowDialog={setShowDeleteDialog}
        projectId={project._id}
      />
      <MoveProjectToFolderDialog
        showDialog={showMoveDialog}
        setShowDialog={() => setShowMoveDialog(false)}
        projectId={project._id}
        projectFolderId={project.folderId}
        onSuccess={handleMoveProjectToFolderSuccess}
      />
    </>
  );
};

export default ProjectListRow;
